import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { core, mh, useMhStream } from "@/lib/mh";
import { errText, BACKEND } from "@/lib/http";
import { Panel, Eyebrow, timeAgo } from "@/components/mh/bits";
import { Btn, Input, Copyable } from "@/components/mh/controls";
import { useDeptMap } from "@/components/mh/hooks";

const DeptIsolation = ({ dmap, setDmap, def, canEdit }) => {
  const deps = useDeptMap();
  return (
    <details className="rounded-lg border border-white/[0.07] bg-white/[0.015] px-4 py-3 group" data-testid="dept-isolation">
      <summary className="cursor-pointer text-sm flex items-center gap-2 list-none">
        <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-[#D4FF3A]/40 text-[#D4FF3A]">PREMIUM</span>
        Isolate sensitive departments on their own OpenClaw agent
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">{Object.values(dmap).filter(Boolean).length} isolated</span>
      </summary>
      <p className="text-xs text-muted-foreground mt-2">Every role in an isolated department runs on that agent (its own tools, memory and permissions). A per-agent mapping below still wins.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-3">
        {Object.values(deps).map((d) => (
          <label key={d.slug} className="text-xs space-y-1">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rotate-45" style={{ background: d.hex }} />{d.name}</span>
            <input value={dmap[d.slug] || ""} placeholder={def} disabled={!canEdit} onChange={(e) => setDmap((m) => ({ ...m, [d.slug]: e.target.value }))} data-testid={`dept-map-${d.slug}`}
              className="w-full h-8 px-2.5 rounded-md bg-white/[0.03] border border-white/10 text-xs font-mono outline-none focus:border-white/30" />
          </label>
        ))}
      </div>
    </details>
  );
};

const CheckBadge = ({ c, slug }) => !c ? null : (
  <span data-testid={`map-check-${slug}`} title={c.error || `run ${c.runId || "—"}`} className="font-mono text-[10px]" style={{ color: c.ok ? "#D4FF3A" : "#FF3B5C" }}>
    {c.ok ? "✓ reached" : "✕ failed"} · {timeAgo(c.at)}
  </span>
);

export const OpenClawMapping = ({ data, canEdit }) => {
  const r = data.runtime;
  const live = r.mode === "own" && !!r.base_url;
  const [agents, setAgents] = useState([]);
  const [map, setMap] = useState(r.agent_map || {});
  const [def, setDef] = useState(r.default_agent_id || "main");
  const [path, setPath] = useState(r.hooks_path || "/hooks");
  const [all, setAll] = useState(false);
  const [checks, setChecks] = useState(r.agent_checks || {});
  const [busy, setBusy] = useState(null);
  const [dmap, setDmap] = useState(r.dept_map || {});
  useEffect(() => { mh.agents().then((x) => setAgents(x.data)).catch(() => {}); }, []);
  const shown = useMemo(() => agents.filter((a) => all || a.runtime?.status !== "not_started" || map[a.slug]), [agents, all, map]);
  const distinct = useMemo(() => new Set([def, ...Object.values(map), ...Object.values(dmap)].filter(Boolean)).size, [def, map, dmap]);
  const save = async () => {
    try {
      const res = await core.setMapping({ default_agent_id: def, agent_map: map, dept_map: dmap, hooks_path: path });
      toast.success(`Mapping saved · ${res.data.mapped} agents mapped · ${res.data.isolated_departments} departments isolated`);
    } catch (e) { toast.error(errText(e)); }
  };
  const ping = async (slug) => {
    setBusy(slug);
    try {
      const c = (await core.pingAgent(slug, map[slug] || def)).data;
      setChecks((x) => ({ ...x, [slug]: c }));
      c.ok ? toast.success(`OpenClaw agent '${c.agentId}' answered`) : toast.error(c.error || "Agent did not answer");
    } catch (e) { toast.error(errText(e)); }
    setBusy(null);
  };
  return (
    <Panel cls="p-5 sm:p-6 space-y-4" testId="openclaw-mapping">
      <div><Eyebrow>Agent mapping</Eyebrow><h3 className="font-display text-2xl mt-1">Atlas agent → your OpenClaw agent</h3>
        <p className="text-sm text-muted-foreground mt-1">Workflow steps and "Run now" go to the mapped OpenClaw agentId. Anything unmapped goes to the default agent. Use Test to send a harmless check to that agent.</p>
        <p className="font-mono text-[11px] text-muted-foreground mt-2" data-testid="mapping-distinct">{distinct} distinct OpenClaw agent{distinct === 1 ? "" : "s"} in use</p></div>
      <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
        <Input label="Default agentId" value={def} onChange={(e) => setDef(e.target.value)} disabled={!canEdit} testId="mapping-default" />
        <Input label="Hooks path" value={path} onChange={(e) => setPath(e.target.value)} disabled={!canEdit} testId="mapping-hooks-path" />
      </div>
      <DeptIsolation dmap={dmap} setDmap={setDmap} def={def} canEdit={canEdit} />
      <div className="max-h-80 overflow-y-auto divide-y divide-white/[0.05] border-y border-white/[0.05]">
        {shown.map((a) => (
          <div key={a.slug} className="flex flex-wrap items-center gap-3 py-2">
            <span className="flex-1 min-w-[10rem] text-sm truncate">{a.name} <span className="font-mono text-[10px] text-muted-foreground">{a.runtime?.status?.replace("_", " ")}</span></span>
            <CheckBadge c={checks[a.slug]} slug={a.slug} />
            <input value={map[a.slug] || ""} placeholder={def} disabled={!canEdit} onChange={(e) => setMap((m) => ({ ...m, [a.slug]: e.target.value }))} data-testid={`map-${a.slug}`}
              className="w-40 h-8 px-2.5 rounded-md bg-white/[0.03] border border-white/10 text-xs font-mono outline-none focus:border-white/30" />
            {canEdit && live && <button onClick={() => ping(a.slug)} disabled={!!busy} data-testid={`map-test-${a.slug}`}
              className="h-8 px-3 rounded-md border border-white/10 text-xs font-mono hover:border-[#00E5FF]/60 hover:text-[#00E5FF] transition-colors disabled:opacity-40">{busy === a.slug ? "Testing…" : "Test"}</button>}
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-muted-foreground py-3">No live or in-dev agents yet. Show all to map any agent.</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Btn onClick={() => setAll((v) => !v)} testId="mapping-show-all">{all ? "Show active only" : "Show all 140"}</Btn>
        <Btn variant="primary" onClick={save} disabled={!canEdit} testId="mapping-save">Save mapping</Btn>
      </div>
    </Panel>
  );
};

export const OpenClawSetup = ({ data }) => {
  const hook = `${BACKEND}/api/mh/webhooks/openclaw/${data.workspace_id}`;
  const ids = [...new Set([data.runtime.default_agent_id || "main", ...Object.values(data.runtime.agent_map || {}), ...Object.values(data.runtime.dept_map || {})])].map((x) => `"${x}"`).join(", ");
  const cfg = `{\n  hooks: {\n    enabled: true,\n    token: "<long-random-hook-token>",   // paste the same token into Manahouse\n    path: "/hooks",\n    allowedAgentIds: [${ids}],   // every agentId you mapped\n    allowRequestSessionKey: false,\n  },\n}`;
  const report = `curl -X POST ${hook} \\\n  -H 'Content-Type: application/json' \\\n  -H 'X-Manahouse-Secret: ${data.runtime.webhook_secret || "<secret>"}' \\\n  -d '{"event":"run.completed","agentId":"main","run_id":"<runId>","status":"ok"}'`;
  return (
    <Panel cls="p-5 sm:p-6 space-y-4" testId="openclaw-setup">
      <div><Eyebrow>Setup snippets</Eyebrow><h3 className="font-display text-2xl mt-1">Wire OpenClaw both ways</h3></div>
      <div><div className="text-sm font-medium">1 · Let Manahouse send work (OpenClaw config)</div>
        <pre className="mt-2 p-3 rounded-lg bg-black/40 border border-white/[0.06] text-xs font-mono whitespace-pre-wrap" data-testid="setup-config">{cfg}</pre></div>
      <div><div className="text-sm font-medium">2 · Report activity back (from an OpenClaw hook or automation)</div>
        <p className="text-xs text-muted-foreground mt-1">Send run.started, run.completed or run.failed (with agent_slug or a mapped agentId) to the event address. Example:</p>
        <pre className="mt-2 p-3 rounded-lg bg-black/40 border border-white/[0.06] text-xs font-mono whitespace-pre-wrap break-all" data-testid="setup-report">{report}</pre></div>
      {data.runtime.webhook_secret && <Copyable label="Event address" value={hook} testId="setup-hook-url" onCopy={() => toast.success("Copied")} />}
      <p className="text-xs text-muted-foreground">Every brief Manahouse sends includes a one-time receipt link. The agent posts what it did (summary, actions, records, links) to it — no secret needed — and the receipt shows on the run, the workflow and the approval desk. You can also include a "receipt" object in run.completed.</p>
    </Panel>
  );
};

export const OpenClawEvents = ({ wsId, canEdit, standin }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { core.runtimeEvents().then((r) => setItems(r.data)).catch(() => {}); }, []);
  useMhStream((ev) => { if (ev.type === "openclaw.event") setItems((l) => [ev.event, ...l].slice(0, 40)); });
  return (
    <Panel cls="p-5 sm:p-6" testId="openclaw-events">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><Eyebrow>Live event log</Eyebrow><h3 className="font-display text-2xl mt-1">What's crossing the wire</h3></div>
        {standin && canEdit && <Btn testId="simulate-activity-btn" onClick={() => core.simulateActivity(wsId).then((r) => toast.success(`Stand-in is reporting runs for ${r.data.agents.length} agents`)).catch((e) => toast.error(errText(e)))}>Simulate OpenClaw activity</Btn>}
      </div>
      <ul className="mt-3 divide-y divide-white/[0.05] font-mono text-[11px]">
        {items.length === 0 && <li className="py-3 text-muted-foreground font-sans text-sm">No events yet.</li>}
        {items.map((e) => (
          <li key={e.id} className="py-2 flex items-center gap-3 mh-enter" data-testid="openclaw-event-row">
            <span className={e.direction === "out" ? "text-[#00E5FF]" : "text-[#D4FF3A]"}>{e.direction === "out" ? "→ OUT" : "← IN"}</span>
            <span className="text-foreground">{e.event_type}</span>
            <span className="truncate text-muted-foreground flex-1">{e.data?.agent_slug || e.data?.agentId || ""}{e.data?.runId ? ` · ${e.data.runId}` : ""}{e.data?.error ? ` · ${e.data.error}` : ""}</span>
            <span style={{ color: e.ok ? "#6B6B7B" : "#FF3B5C" }}>{e.ok ? "ok" : "error"}</span>
            <span className="text-muted-foreground">{timeAgo(e.received_at)}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
};
